import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { StashCard, StashCategory } from '@stash/shared-types';
import { saveCategoryAsync, saveCategoriesAsync } from '../actions/stashcard-actions';

// Runtime data includes an `id` field not present in the StashCategory type definition
export interface CategoryEntry extends StashCategory {
  id?: string;
}

// Runtime data includes an `id` field not present in the StashCard type definition
interface StashCardEntry extends StashCard {
  id?: string;
}

export interface StashcardState {
  categories: CategoryEntry[];
  stashcards: StashCardEntry[];
  isLoading: boolean;
  error: string | null;
  syncInProgress: boolean;
  syncType: string | null;
}

const initialState: StashcardState = {
  categories: [],
  stashcards: [],
  isLoading: false,
  error: null,
  syncInProgress: false,
  syncType: null,
};

function upsertCategories(state: StashcardState, newCategories: CategoryEntry[]): void {
  newCategories.forEach((newCategory) => {
    const existingIndex = state.categories.findIndex((cat) => cat.id === newCategory.id);
    if (existingIndex !== -1) {
      state.categories[existingIndex] = newCategory;
    } else {
      state.categories.push(newCategory);
    }
  });
}

const stashcardSlice = createSlice({
  name: 'stashcard',
  initialState,
  reducers: {
    saveCategoriesStarted: (state, action: PayloadAction<{ syncType?: string } | undefined>) => {
      state.isLoading = true;
      state.syncInProgress = true;
      state.syncType = action.payload?.syncType ?? 'CATEGORIES_SAVE';
      state.error = null;
    },
    saveCategoriesSuccess: (state, action: PayloadAction<CategoryEntry[] | undefined>) => {
      state.isLoading = false;
      state.syncInProgress = false;
      state.syncType = null;
      if (action.payload) {
        state.categories = [...state.categories, ...action.payload];
      }
    },
    saveCategoriesFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.syncInProgress = false;
      state.syncType = null;
      state.error = action.payload;
    },
    loadStashcards: (state, action: PayloadAction<StashCardEntry[]>) => {
      state.stashcards = action.payload;
    },
    addStashcard: (state, action: PayloadAction<StashCardEntry>) => {
      state.stashcards.push(action.payload);
    },
    updateStashcard: (state, action: PayloadAction<StashCardEntry>) => {
      const index = state.stashcards.findIndex((card) => card.id === action.payload.id);
      if (index !== -1) {
        state.stashcards[index] = action.payload;
      }
    },
    removeStashcard: (state, action: PayloadAction<string>) => {
      state.stashcards = state.stashcards.filter((card) => card.id !== action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveCategoryAsync.pending, (state: StashcardState) => {
        state.isLoading = true;
        state.syncInProgress = true;
        state.syncType = 'CATEGORIES_SAVE';
        state.error = null;
      })
      .addCase(saveCategoryAsync.fulfilled, (state: StashcardState, action: PayloadAction<CategoryEntry | CategoryEntry[] | undefined>) => {
        state.isLoading = false;
        state.syncInProgress = false;
        state.syncType = null;
        if (action.payload) {
          const newCategories = Array.isArray(action.payload) ? action.payload : [action.payload];
          upsertCategories(state, newCategories);
        }
      })
      .addCase(saveCategoryAsync.rejected, (state: StashcardState, action: PayloadAction<string | undefined>) => {
        state.isLoading = false;
        state.syncInProgress = false;
        state.syncType = null;
        state.error = action.payload ?? null;
      })
      .addCase(saveCategoriesAsync.pending, (state: StashcardState) => {
        state.isLoading = true;
        state.syncInProgress = true;
        state.syncType = 'CATEGORIES_SAVE';
        state.error = null;
      })
      .addCase(saveCategoriesAsync.fulfilled, (state: StashcardState, action: PayloadAction<CategoryEntry | CategoryEntry[] | undefined>) => {
        state.isLoading = false;
        state.syncInProgress = false;
        state.syncType = null;
        if (action.payload) {
          const newCategories = Array.isArray(action.payload) ? action.payload : [action.payload];
          upsertCategories(state, newCategories);
        }
      })
      .addCase(saveCategoriesAsync.rejected, (state: StashcardState, action: PayloadAction<string | undefined>) => {
        state.isLoading = false;
        state.syncInProgress = false;
        state.syncType = null;
        state.error = action.payload ?? null;
      });
  },
});

export const {
  saveCategoriesStarted,
  saveCategoriesSuccess,
  saveCategoriesFailure,
  loadStashcards,
  addStashcard,
  updateStashcard,
  removeStashcard,
  clearError,
  setLoading,
} = stashcardSlice.actions;

export default stashcardSlice.reducer;
